/**
 * Responsibility: where the grading work has got to, without reading a transcript.
 *
 * **The state of this work lived in a terminal, tangled with the code
 * changes that produced it.** That is a poor place for the one thing worth
 * checking weekly - which metrics work, how well, and what is being done to
 * the ones that do not - so it lives here instead, and the figures are
 * recomputed from the corpus on every load rather than typed by hand.
 *
 * **Measured and editorial are kept apart on purpose.** Accuracy, the card
 * counts and the completeness rubric are arithmetic. What is underway and
 * what is blocking are somebody's words, and the panel labels them, because
 * a page that mixes the two teaches a reader to trust neither.
 *
 * Grouped as it is because the top row is a prerequisite: nothing can be
 * measured better than the card can be found, and nine measurement
 * approaches were rejected on evidence produced while that was broken.
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { hubApi } from '../../api/client';
import { MetricProgressTable } from './MetricProgressTable';

/** A minute: the corpus only changes as fast as the harvest writes to it. */
const STALE_MS = 60_000;

export function MetricProgressCard() {
  const progress = useQuery({
    queryKey: ['calibration-progress'],
    queryFn: hubApi.calibrationProgress,
    staleTime: STALE_MS,
  });

  if (progress.isError) {
    return <Alert severity="error">Could not read the calibration progress.</Alert>;
  }
  if (!progress.data) {
    return (
      <Typography variant="body2" color="text.secondary">
        Reading the corpus...
      </Typography>
    );
  }

  const { metrics, walk, cards_measured, within_points, good_enough } = progress.data;
  const underway = metrics.filter((metric) => metric.underway);

  return (
    <Stack spacing={3} sx={{ pb: 2 }}>
      <Box>
        <Typography variant="h6">Grading metrics</Typography>
        <Typography variant="body2" color="text.secondary">
          A grade is four facets and only one of them is measured today. Every figure
          below is recomputed from {cards_measured} cards measured by walk {walk}, against
          what the grader published for the same cards. Agreement means within{' '}
          {within_points} points; a metric counts as working at {good_enough}%.
        </Typography>
      </Box>

      <MetricProgressTable metrics={metrics} within={within_points} />

      <Divider />

      <Box>
        <Typography variant="subtitle2">What is underway</Typography>
        <Typography variant="caption" color="text.secondary">
          Written by hand, unlike the table above. Treat it as a note, not a measurement.
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        {underway.map((metric) => (
          <Paper key={metric.key} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {metric.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {metric.underway}
            </Typography>
            {metric.blocked_by ? (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                Blocked by: {metric.blocked_by}
              </Typography>
            ) : null}
            {metric.notes.map((note) => (
              <Typography key={note} variant="caption" color="text.secondary" display="block">
                {note}
              </Typography>
            ))}
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}
